import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import './ScenarioDoor.css';
import { fetchScenarioScenes, startScenarioRun, startScenarioRunAt } from './api';
import { useCapabilities } from './CapabilitiesContext';
import { playedScenario } from './scenarioScreens';
import {
  HAUPTBAHNHOF_SCENARIO,
  SCENARIOS_PATH,
  scenarioRunPath,
  sceneName,
} from './scenes';

/**
 * What the Gleiswechsel may be asked to be, beside leaving it to the coin.
 *
 * The empty string is the coin, because that is what an unpressed radio group reads as and what a
 * request with nothing to say sends: a run nobody pinned gets a change about half the time, which
 * is the station as a student meets it.
 */
const FORCED_CHANGE = ['', 'CHANGED', 'UNCHANGED'];

/**
 * The door of one scenario: where its walk is read and a run of it is started.
 *
 * A run is bought by a decision rather than by a navigation, which is the whole of what this
 * screen is for. It shows the scenes the scenario walks, in the order it walks them, and offers
 * the ones the student may go in at — where they have already been, and the top of the walk, which
 * is on offer to everyone. The rest are shown all the same: what is still ahead of a student is
 * part of what a scenario is, and a door that hid it would be a scenario with no shape.
 *
 * Where the student has been is the server's word and is never guessed at here; the only rule this
 * adds to it is the one the walk itself carries, that the top of a walk is on offer to everyone.
 * The rest of what it decides is which door to knock on: the top is the plain one, anywhere
 * further along is the one that names the scene.
 */
export default function ScenarioDoor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scenarioKey } = useParams();
  // What this build plays under that key, which is known before the server is asked anything.
  const played = playedScenario(scenarioKey);
  const [walk, setWalk] = useState(null);
  const [failed, setFailed] = useState(false);
  // Whether a run is being opened. A run is a thing that happened and is paid for, so the second
  // press of a double-press must buy nothing: every way in is shut for as long as one is opening.
  const [going, setGoing] = useState(false);
  // What the round about to be drawn must come out as, where this deployment lets a run be pinned
  // at all. Memory-only and reset by leaving the door: it says what the next run is, not what runs
  // of this scenario are.
  const [platformChange, setPlatformChange] = useState('');
  const { characterStub } = useCapabilities();

  // Whether the round may be pinned here: only where the development panel is served, and only at
  // the station, because a platform change is the station's and means nothing at a bakery. The
  // server refuses the rest anyway — this is what stops the screen offering what would be dropped.
  const pinnable = characterStub && scenarioKey === HAUPTBAHNHOF_SCENARIO;

  useEffect(() => {
    if (!played) {
      return undefined;
    }
    let gone = false;
    fetchScenarioScenes(scenarioKey)
      .then((scenario) => {
        if (!gone) setWalk(scenario.scenes ?? []);
      })
      .catch(() => {
        if (!gone) setFailed(true);
      });
    return () => {
      gone = true;
    };
  }, [played, scenarioKey]);

  // Going in, at the scene named or at the top of the walk when nothing is. The run's own path
  // replaces the door in the history rather than being stacked on top of it: a student pressing
  // back leaves the scenario instead of buying another run.
  const goIn = useCallback(
    (sceneKey) => {
      setGoing(true);
      // Nothing asked for sends nothing, so a run opened without touching the control is opened
      // exactly as it always was.
      const bahn = pinnable && platformChange ? { platformChange } : null;
      return (sceneKey
        ? startScenarioRunAt(scenarioKey, sceneKey, bahn)
        : startScenarioRun(scenarioKey, null, bahn))
        .then((started) =>
          navigate(scenarioRunPath(scenarioKey, started.runId), { replace: true }),
        )
        .catch(() => {
          setGoing(false);
          setFailed(true);
        });
    },
    [navigate, scenarioKey, pinnable, platformChange],
  );

  const message = (heading, explain) => (
    <div className="scenario-door-message">
      <h1>{heading}</h1>
      <p>{explain}</p>
      <button type="button" onClick={() => navigate(SCENARIOS_PATH)}>
        {t('player.scenarios')}
      </button>
    </div>
  );

  if (!played) {
    return message(t('player.unknown.heading'), t('player.unknown.explain'));
  }
  if (failed) {
    return message(t('player.failed.heading'), t('player.failed.explain'));
  }
  if (!walk) {
    return null;
  }

  return (
    <div className="chat-list-container">
      <div className="chat-list-header">
        <h2>
          {t(played.screen.title)} <small>{t(played.screen.subtitle)}</small>
        </h2>
      </div>
      <div className="chat-list-content">
        <p className="scenario-door-intro">{t('scenarioDoor.intro')}</p>
        {/* Above the ways in because it bears on all of them alike: it says what the round the
            next press draws must be, whichever scene that press goes in at. */}
        {pinnable && (
          <fieldset className="scenario-door-forced">
            <legend>{t('scenarioDoor.forced.legend')}</legend>
            <p className="scenario-door-forced-note">{t('scenarioDoor.forced.note')}</p>
            {FORCED_CHANGE.map((choice) => (
              <label key={choice || 'CHANCE'}>
                <input
                  type="radio"
                  name="platformChange"
                  value={choice}
                  checked={platformChange === choice}
                  disabled={going}
                  onChange={() => setPlatformChange(choice)}
                />
                {t(`scenarioDoor.forced.${choice || 'CHANCE'}`)}
              </label>
            ))}
          </fieldset>
        )}
        <ul className="chat-list" aria-label={t('scenarioDoor.ways')}>
          {walk.map((scene, at) => {
            // The first scene of a walk is the way in from the top, which every student has,
            // however far they have got. Every other one is somewhere they have to have been.
            const top = at === 0;
            const open = top || scene.reached;
            return (
              <li key={scene.key} className="chat-list-item scenario-list-item scenario-door-way">
                <button
                  type="button"
                  disabled={!open || going}
                  onClick={() => goIn(top ? null : scene.key)}
                >
                  <span className="chat-list-item-name">{sceneName(t, scene.key)}</span>
                  {!open && (
                    <span className="scenario-list-item-subtitle">
                      {t('scenarioDoor.outOfReach')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
